import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import { StrikeInput } from '@/App/tiles/Option/common/StrikeInput.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import { getQuoteType, selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { LegId, OptionStrike, SolvingStrike } from '@/store/option/state.ts';
import { requestOptionsRfsThunk } from '@/store/option/thunks/thunk.ts';

type StrikeProps = {
  legId: LegId;
  dataE2e: string;
  className?: string;
};

export function Strike({ legId, dataE2e, className }: StrikeProps) {
  const [checkOption] = useCheckOption();
  const dispatch = useAppDispatch();
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { tileId, strike, forceTradeable } = useOptionSelector((option) => {
    const legProperties = selectOptionLegsProperties(option, legId);
    return {
      tileId: option.properties.tileId,
      strike: legProperties?.strike,
      forceTradeable: getQuoteType(option) === 'Tradeable',
    };
  });

  function handleStrikeChange(strike: OptionStrike | SolvingStrike | null) {
    sgwtWebAnalytics?.trackEvent('Option', 'Strike change', `${strike}`);

    if (strike?.type === 'solving') {
      // solving is requested directly to Multipass, without going through Trade Capture
      // (isReadyToPrice must not gate the solving RFS request)
      dispatch(
        optionSlice.actions.setSolving({
          tileId,
          legId,
          solvingStrike: strike,
        }),
      );
      dispatch(requestOptionsRfsThunk({ tileId, forceTradeable }));
      return;
    }
    checkOption({
      tileId,
      legProperties: {
        legId,
        strike,
        premiumBid: null,
        premiumAsk: null,
      },
    });
  }

  return (
    <StrikeInput
      data-e2e={dataE2e}
      legId={legId}
      onValueChange={handleStrikeChange}
      value={strike}
      className={className}
    />
  );
}
